import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'react-hot-toast';
import confetti from 'canvas-confetti';
import { createOrder } from '../api';
import GuestCheckout from '../components/GuestCheckout';
import LoginSheet from '../components/LoginSheet';
import PhoneCapture from '../components/PhoneCapture';
import { useAuth } from '../AuthContext';
import { haptics } from '../utils/haptics';
import { getMenuBundle } from '../menuApi';
import { evaluatePauseState } from '../utils/pauseRules';
import { CATEGORIES } from '../menuData';
import { DishPlaceholder } from '../components/FoodArt';
import { COLOR, FONT, glass } from '../theme';

// Menu item thumbnail: real photo when the item has one, a clean SVG dish
// placeholder otherwise (also falls back to it if the photo fails to load).
function FoodThumb({ image, name }) {
  const [ok, setOk] = useState(Boolean(image));
  if (ok) {
    return (
      <img
        src={image}
        alt={name}
        onError={() => setOk(false)}
        loading="lazy"
        decoding="async"
        width={700}
        height={700}
        style={styles.foodImg}
      />
    );
  }
  return <DishPlaceholder size={80} tone="rgba(245,245,247,0.25)" fill="rgba(255,255,255,0.06)" />;
}

function GuestMenu() {
  const { hotelId, roomNumber, restaurantId } = useParams();
  const navigate = useNavigate();
  const { user, hasPhone, refreshAuth } = useAuth();
  const requireLogin = localStorage.getItem('setting_requireLogin') !== 'false';
  const cartKey = `gapgo_cart_${hotelId}_${roomNumber}`;
  const [activeCategory, setActiveCategory] = useState('Entrees');
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(`gapgo_cart_${hotelId}_${roomNumber}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [showCart, setShowCart] = useState(false);
  const [ordered, setOrdered] = useState(false);
  const [orderRef, setOrderRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showPhoneCapture, setShowPhoneCapture] = useState(false);
  const [orderNote, setOrderNote] = useState('');
  // True while we're handing the guest back from Google sign-in into checkout.
  // Lets us show one continuous loader instead of flashing the menu in between.
  const [resumeFailed, setResumeFailed] = useState(false);
  // True only if a checkout was already pending when this page first mounted — i.e.
  // we've just been reloaded back from Google sign-in. Clicking "Checkout" in-session
  // ALSO sets pendingCheckout, but we must not show the resume loader then or it hides
  // the sign-in sheet (and never clears). We only resume on an actual OAuth return.
  const [pendingAtMount] = useState(() => localStorage.getItem('pendingCheckout') === 'true');
  const [liveMenu, setLiveMenu] = useState([]);
  const menuStorageKey = `gapgo_menu_${hotelId || 'default'}`;
  
  // Service hours as the SERVER states them, and the state they evaluate to right
  // now. `null` means we have not heard yet — treated as no restrictions, which is
  // the same thing the guest saw before and fails open rather than blocking orders
  // on a network blip.
  const [serverPause, setServerPause] = useState(null);
  const [pauseState, setPauseState] = useState({ paused: false, availableCategories: [], reason: null });

  const refreshMenu = useCallback(async (active = true) => {
    try {
      // restaurantId comes off the QR code URL and was previously read and thrown
      // away here. Passing it turns this — the highest-traffic read in the product,
      // one per guest page load — from a full-table Scan into an indexed Query, and
      // scopes the menu to the restaurant the guest actually chose.
      const { items, pause } = await getMenuBundle(hotelId, restaurantId);
      if (!active) return;
      setLiveMenu(items);
      // Service hours arrive WITH the menu. They used to be read from localStorage,
      // which on a guest's phone was always empty — so a closed restaurant looked
      // open and every item looked orderable. See utils/pauseRules.js.
      if (pause) setServerPause(pause);
    } catch (e) {
      // keep last good menu
    }
  }, [hotelId, restaurantId]);

  // Calculate pause state from rules engine
  useEffect(() => {
    const evaluate = () => {
      const state = evaluatePauseState(serverPause, 'AEST');
      setPauseState(state);
    };
    evaluate();
    // Re-evaluate every minute so a rule that starts at 22:00 takes effect without
    // the guest reloading.
    const timer = setInterval(evaluate, 60000);
    return () => clearInterval(timer);
  }, [serverPause]);

  // Persist cart to localStorage so it survives the OAuth redirect
  useEffect(() => {
    if (Object.keys(cart).length > 0) {
      localStorage.setItem(cartKey, JSON.stringify(cart));
    } else {
      localStorage.removeItem(cartKey);
    }
  }, [cart, cartKey]);

  // Handle return from redirect-based payment (Klarna, AfterPay, etc.).
  // Stripe appends ?payment_intent=pi_xxx&redirect_status=succeeded to the return_url.
  // We store the PI ID, clean the URL, then place the order once the user is ready.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const piId = params.get('payment_intent');
    const status = params.get('redirect_status');
    if (piId && status === 'succeeded') {
      window.history.replaceState({}, '', window.location.pathname);
      localStorage.setItem('completedPaymentIntentId', piId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const piId = localStorage.getItem('completedPaymentIntentId');
    if (!piId || !user || Object.keys(cart).length === 0) return;
    localStorage.removeItem('completedPaymentIntentId');
    clearResume();
    handlePlaceOrder({
      name: user.name, email: user.email,
      phone: user.phone || '', hotelId,
      paymentIntentId: piId,
    });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the guest just returned from Google sign-in and auth hasn't loaded yet,
  // poll until Amplify finishes initialising the session, then the effect below
  // resumes checkout. pendingCheckout is the signal that a checkout was interrupted.
  useEffect(() => {
    if (localStorage.getItem('pendingCheckout') !== 'true') return;
    if (user) return; // already signed in — the effect below handles it

    let stopped = false;
    let attempts = 0;
    const poll = async () => {
      if (stopped) return;
      attempts++;
      const ok = await refreshAuth();
      if (ok || stopped) return; // user set — open-checkout effect takes over
      if (attempts < 20) setTimeout(poll, 300);
      else setResumeFailed(true); // give up after ~6s — stop blocking on the loader
    };
    poll();
    return () => { stopped = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearResume = () => {
    localStorage.removeItem('pendingCheckout');
  };

  // Came back from Google but the session never established (exchange failed, or the
  // guest returned in a different browser context). AuthCallback flags this — don't
  // strand them on the resume loader; drop it and re-open sign-in so they can retry.
  useEffect(() => {
    if (localStorage.getItem('oauthFailed') !== 'true') return;
    localStorage.removeItem('oauthFailed');
    setResumeFailed(true);
    if (Object.keys(cart).length > 0) {
      setShowLogin(true);
      toast('Sign-in didn’t finish — please try again', {
        style: { background: '#141416', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' },
      });
    } else {
      clearResume();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net for the silent-timeout case: the resume poll gave up but a checkout is
  // still pending and we have a cart — surface sign-in again rather than a dead menu.
  useEffect(() => {
    if (!resumeFailed || user) return;
    if (localStorage.getItem('pendingCheckout') !== 'true') return;
    if (Object.keys(cart).length === 0) { clearResume(); return; }
    setShowLogin(true);
  }, [resumeFailed, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resume after sign-in: go STRAIGHT to payment — no "thanks for signing in"
  // interstitial. pendingCheckout stays set until payment is entered/cancelled,
  // so this survives the remounts Amplify causes on the OAuth return. Opening the
  // sheet is idempotent, so re-running on remount is harmless.
  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem('pendingCheckout') !== 'true') return;
    if (Object.keys(cart).length === 0) { clearResume(); return; } // nothing to buy
    setShowCart(false);
    setShowLogin(false);
    if (!hasPhone) setShowPhoneCapture(true);
    else setShowCheckout(true);
  }, [user, hasPhone, cart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the live menu from the shared menu source so kitchen changes appear here.
  useEffect(() => {
    let active = true;
    refreshMenu(active);

    const onStorage = (e) => {
      if (e.key === menuStorageKey) refreshMenu(active);
    };
    const onMenuUpdated = (e) => {
      if (!e?.detail || e.detail.hotelId === hotelId) refreshMenu(active);
    };
    const onBroadcast = (e) => {
      if (!e?.data || e.data.hotelId === hotelId) refreshMenu(active);
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('gapgo-menu-updated', onMenuUpdated);
    const channel = 'BroadcastChannel' in window ? new BroadcastChannel('gapgo-menu-sync') : null;
    if (channel) channel.addEventListener('message', onBroadcast);
    const poll = setInterval(() => refreshMenu(active), 10000);

    return () => {
      active = false;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('gapgo-menu-updated', onMenuUpdated);
      if (channel) {
        channel.removeEventListener('message', onBroadcast);
        channel.close();
      }
      clearInterval(poll);
    };
  }, [hotelId, menuStorageKey, refreshMenu]);

  // (A `storage` listener used to live here to pick up pause-rule changes written by
  // another tab. It is gone because the rules are no longer in localStorage — they
  // arrive from the server with the menu, and `refreshMenu` already polls. On a
  // guest's phone that listener could never have fired anyway: nothing on that device
  // ever wrote the key.)

  // Only show in-stock items to guests; filter further if pause mode is active
  const visibleMenu = liveMenu.filter((i) => {
    if (i.inStock === false) return false;
    if (pauseState.paused && !pauseState.availableCategories.includes(i.category)) return false;
    return true;
  });

  // Group by category for display
  const grouped = {};
  const categoryOrder = [
    ...CATEGORIES,
    ...visibleMenu.map((i) => i.category).filter((cat, idx, arr) => cat && !CATEGORIES.includes(cat) && arr.indexOf(cat) === idx),
  ];
  categoryOrder.forEach((cat) => {
    const items = visibleMenu.filter((i) => i.category === cat);
    if (items.length) grouped[cat] = items;
  });
  const categories = Object.keys(grouped);

  // Reset active category if it got hidden
  useEffect(() => {
    if (categories.length && !grouped[activeCategory]) {
      setActiveCategory(categories[0]);
    }
    // `categories.join(',')` is intentional: `categories` is a fresh array every
    // render, so depending on it directly would re-run this on every render and
    // fight the user's own category taps. The joined string is stable when the
    // category SET is unchanged, which is the only thing this effect cares about.
    // `grouped` is read but not depended on for the same reason — it is rebuilt
    // each render from the same source. Guest-flow files are stabilised after
    // three painful reverts (see CLAUDE.md); this is a comment-only change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.join(','), activeCategory]);

  const addToCart = (item) => {
    haptics.light();
    setCart(prev => ({
      ...prev,
      [item.id]: { ...item, qty: (prev[item.id]?.qty || 0) + 1 }
    }));
  };

  const removeFromCart = (item) => {
    haptics.light();
    setCart(prev => {
      const updated = { ...prev };
      if (updated[item.id]?.qty > 1) {
        updated[item.id] = { ...updated[item.id], qty: updated[item.id].qty - 1 };
      } else {
        delete updated[item.id];
      }
      return updated;
    });
  };

  const cartItems = Object.values(cart);
  const totalItems = cartItems.reduce((sum, i) => sum + i.qty, 0);
  const totalPrice = cartItems.reduce((sum, i) => sum + i.price * i.qty, 0);

  const fireConfetti = () => {
    confetti({
      particleCount: 80, spread: 70, origin: { y: 0.6 },
      colors: [COLOR.accent, COLOR.accentDeep, '#F5F5F7'],
    });
  };

  const handlePlaceOrder = async (guestInfo) => {
    setLoading(true);
    try {
      const res = await createOrder({
        hotelId, roomNumber, restaurantId, type: 'food',
        items: cartItems.map(item => ({ name: item.name, qty: item.qty, price: item.price })),
        total: totalPrice,
        guestName: guestInfo.name, guestPhone: guestInfo.phone,
        guestEmail: guestInfo.email, marketingConsent: guestInfo.consent,
        paymentIntentId: guestInfo.paymentIntentId, paymentStatus: 'paid',
        notes: orderNote || '',
      });
      // The reference the guest needs if they ever have to ask us about this
      // order. It used to be received here and discarded, leaving a guest with
      // literally nothing once they closed the tab.
      if (res && res.orderId) setOrderRef(res.orderId);
      haptics.success();
      fireConfetti();
      toast.success('Order placed!', {
        style: { background: '#141416', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' },
        iconTheme: { primary: '#22C55E', secondary: '#0a0a0a' },
      });
      localStorage.removeItem(cartKey);
      clearResume();
      setLoading(false);
      setShowCheckout(false);
      setShowCart(false);
      setOrdered(true);
    } catch (error) {
      haptics.warning();
      toast.error('Something went wrong. Please try again.', {
        style: { background: '#141416', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' },
      });
      setLoading(false);
    }
  };

  if (ordered) {
    return (
      <div style={styles.page}>
        <Toaster position="top-center" />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={styles.successPage}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring' }} style={styles.successIconWrap}>
            <span style={{ fontSize: '40px' }}>🍽️</span>
          </motion.div>
          <h1 style={styles.successTitle}>Order Placed!</h1>
          <p style={styles.successSub}>Your food is being prepared</p>
          <div style={styles.successCard}>
            {cartItems.map((item, i) => (
              <div key={i} style={styles.successRow}>
                <span style={styles.successItemName}>{item.qty}x {item.name}</span>
                <span style={styles.successItemPrice}>${(item.price * item.qty).toFixed(2)}</span>
              </div>
            ))}
            <div style={styles.successDivider} />
            <div style={styles.successRow}>
              <span style={{ ...styles.successItemName, color: COLOR.text, fontWeight: '700' }}>Total</span>
              <span style={{ ...styles.successItemPrice, color: COLOR.text, fontWeight: '700', fontSize: '18px' }}>${totalPrice.toFixed(2)}</span>
            </div>
            <div style={styles.successDivider} />
            {orderRef && (
              <div style={styles.successRow}>
                <span style={styles.successLabel}>ORDER</span>
                <span style={{ ...styles.successValue, fontVariantNumeric: 'tabular-nums' }}>{orderRef}</span>
              </div>
            )}
            <div style={styles.successRow}>
              <span style={styles.successLabel}>ROOM</span>
              <span style={styles.successValue}>Room {roomNumber}</span>
            </div>
            <div style={styles.successRow}>
              <span style={styles.successLabel}>PAYMENT</span>
              <span style={{ ...styles.successValue, color: '#16A34A' }}>Paid</span>
            </div>
            <div style={styles.successRow}>
              <span style={styles.successLabel}>ETA</span>
              <span style={{ ...styles.successValue, color: '#16A34A' }}>20-30 mins</span>
            </div>
          </div>
          <motion.button whileTap={{ scale: 0.97 }} style={styles.backBtn} onClick={() => navigate('/' + hotelId + '/' + roomNumber)}>
            Back to Services
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Returning from Google sign-in: bridge the gap with one continuous loader
  // (instead of flashing the menu) until the payment/phone sheet actually opens.
  // We hold the loader until then — not just until `user` loads — so the client-side
  // return lands straight on the loader → payment sheet, with no menu flash between.
  // Gated on pendingAtMount (so an in-session Checkout click never triggers it) AND the
  // live flag (so cancelling the sheet clears the loader instead of re-trapping on it).
  const stillPending = localStorage.getItem('pendingCheckout') === 'true';
  const isResuming = pendingAtMount && stillPending && totalItems > 0 && !resumeFailed
    && !showCheckout && !showPhoneCapture && !showLogin;
  if (isResuming) {
    return (
      <div style={styles.resumePage}>
        <Toaster position="top-center" />
        <motion.div
          style={styles.resumeRing}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, ease: 'linear', duration: 0.9 }}
        />
        <h1 style={styles.resumeTitle}>Getting your order ready…</h1>
        <p style={styles.resumeSub}>One moment while we sign you in</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Toaster position="top-center" />

      <div style={styles.header}>
        <motion.button whileTap={{ scale: 0.95 }} style={styles.backArrow} onClick={() => { haptics.light(); navigate('/' + hotelId + '/' + roomNumber); }}>
          Back
        </motion.button>
        <div style={styles.headerCenter}>
          <h1 style={styles.headerTitle}>Room Dining</h1>
          <div style={styles.roomBadge}>
            <span style={styles.roomDot} />
            <span style={styles.roomText}>Room {roomNumber}</span>
          </div>
        </div>
      </div>

      {pauseState.paused && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} style={{
          background: 'rgba(234, 88, 12, 0.1)', border: '1px solid rgba(234, 88, 12, 0.3)', borderRadius: '12px', padding: '14px 18px',
          margin: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>🌙</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#ea580c', margin: '0 0 2px' }}>{pauseState.reason || 'Restaurant Closed'}</p>
            <p style={{ fontSize: '11px', color: '#999', margin: 0 }}>Limited menu available: {pauseState.availableCategories.join(', ')}</p>
          </div>
        </motion.div>
      )}

      {activeCategory === 'Entrees' && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} style={styles.upsellBanner}
          onClick={() => { haptics.light(); if (grouped['Alcohol']) setActiveCategory('Alcohol'); }}>
          <span style={styles.upsellEmoji}>🍷</span>
          <div style={{ flex: 1 }}>
            <p style={styles.upsellTitle}>Add drinks to your order</p>
            <p style={styles.upsellSub}>Wine, beer and cocktails available</p>
          </div>
          <span style={styles.upsellArrow}>›</span>
        </motion.div>
      )}

      <div style={styles.categoryScroll}>
        <div style={styles.categoryRow}>
          {categories.map((cat) => (
            <motion.button key={cat} whileTap={{ scale: 0.95 }}
              onClick={() => { haptics.light(); setActiveCategory(cat); }}
              style={{ ...styles.categoryTab, background: 'transparent', color: activeCategory === cat ? COLOR.text : COLOR.textFaint, fontWeight: activeCategory === cat ? '700' : '600', borderBottom: activeCategory === cat ? `2px solid ${COLOR.accent}` : '2px solid transparent' }}>
              {cat}
            </motion.button>
          ))}
        </div>
      </div>

      <div style={styles.grid}>
        <AnimatePresence mode="wait">
          <motion.div key={activeCategory} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} style={styles.gridInner}>
            {(grouped[activeCategory] || []).map((item, i) => (
              <motion.div key={item.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} style={styles.gridCard}>
                {item.popular && <div style={styles.popularTag}>Popular</div>}
                <div style={styles.imageBox}>
                  <FoodThumb image={item.image} name={item.name} />
                </div>
                <div style={styles.cardInfo}>
                  <p style={styles.itemName}>{item.name}</p>
                  <p style={styles.itemDesc}>{item.description || item.desc}</p>
                  <div style={styles.cardFooter}>
                    <span style={styles.itemPrice}>${item.price.toFixed(2)}</span>
                    {cart[item.id] ? (
                      <div style={styles.qtyRow}>
                        <motion.button whileTap={{ scale: 0.85 }} style={styles.qtyBtn} onClick={() => removeFromCart(item)}>-</motion.button>
                        <motion.span key={cart[item.id].qty} initial={{ scale: 1.3, color: COLOR.text }} animate={{ scale: 1, color: COLOR.text }} style={styles.qtyNum}>{cart[item.id].qty}</motion.span>
                        <motion.button whileTap={{ scale: 0.85 }} style={{ ...styles.qtyBtn, background: COLOR.accent, color: '#fff', border: 'none' }} onClick={() => addToCart(item)}>+</motion.button>
                      </div>
                    ) : (
                      <motion.button whileTap={{ scale: 0.85 }} style={styles.addBtn} onClick={() => addToCart(item)}>+</motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {totalItems > 0 && (
          <motion.div initial={{ opacity: 0, y: 80 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 80 }} style={styles.cartBar} onClick={() => { haptics.medium(); setShowCart(true); }}>
            <motion.div key={totalItems} initial={{ scale: 1.3 }} animate={{ scale: 1 }} style={styles.cartCount}>{totalItems}</motion.div>
            <span style={styles.cartLabel}>View Order</span>
            <span style={styles.cartTotal}>${totalPrice.toFixed(2)}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCart && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={styles.modalOverlay} onClick={() => setShowCart(false)}>
            <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 300 }} style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: '16px 20px 0' }}>
                <div style={styles.modalHandle} />
                <h2 style={styles.modalTitle}>Your Order</h2>
                <p style={styles.modalRoom}>Room {roomNumber}</p>
              </div>

              <div style={styles.modalScroll}>
                <div style={styles.modalItems}>
                  {cartItems.map((item, i) => (
                    <div key={i} style={styles.modalItem}>
                      <div style={styles.modalItemLeft}>
                        <span style={styles.modalItemEmoji}>{item.emoji}</span>
                        <div>
                          <p style={styles.modalItemName}>{item.name}</p>
                          <p style={styles.modalItemPrice}>${item.price.toFixed(2)} each</p>
                        </div>
                      </div>
                      <div style={styles.qtyRow}>
                        <motion.button whileTap={{ scale: 0.85 }} style={styles.qtyBtn} onClick={() => removeFromCart(item)}>-</motion.button>
                        <span style={styles.qtyNum}>{item.qty}</span>
                        <motion.button whileTap={{ scale: 0.85 }} style={{ ...styles.qtyBtn, background: COLOR.accent, color: '#fff', border: 'none' }} onClick={() => addToCart(item)}>+</motion.button>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={styles.noteSection}>
                  <p style={styles.noteLabel}>Special instructions</p>
                  <textarea
                    style={styles.noteTextarea}
                    placeholder="Allergies, spice level, extra sauce…"
                    value={orderNote}
                    onChange={(e) => setOrderNote(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <div style={styles.modalDivider} />
                <div style={styles.modalTotal}>
                  <span style={styles.modalTotalLabel}>Total</span>
                  <span style={styles.modalTotalAmount}>${totalPrice.toFixed(2)}</span>
                </div>
                <motion.button whileTap={{ scale: 0.97 }} style={styles.checkoutBtn} onClick={() => {
                  haptics.medium();
                  if (requireLogin && !user) {
                    localStorage.setItem('pendingCheckout', 'true');
                    setShowCart(false);
                    setShowLogin(true);
                  } else if (requireLogin && !hasPhone) {
                    setShowCart(false);
                    setShowPhoneCapture(true);
                  } else {
                    setShowCart(false);
                    setShowCheckout(true);
                  }
                }}>
                  Checkout - ${totalPrice.toFixed(2)}
                </motion.button>
                <motion.button whileTap={{ scale: 0.97 }} style={styles.continueBtn} onClick={() => setShowCart(false)}>
                  Add More Items
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCheckout && (
          <GuestCheckout total={totalPrice} roomNumber={roomNumber} hotelId={hotelId} restaurantId={restaurantId} items={cartItems.map(i => ({ name: i.name, qty: i.qty, price: i.price }))} notes={orderNote} loading={loading} onConfirm={handlePlaceOrder} onCancel={() => { setShowCheckout(false); clearResume(); }} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLogin && (
          <LoginSheet
            onCancel={() => { setShowLogin(false); clearResume(); }}
            onGuestCheckout={() => { setShowLogin(false); setShowCheckout(true); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPhoneCapture && (
          <PhoneCapture
            onDone={() => { setShowPhoneCapture(false); setShowCheckout(true); }}
            onCancel={() => { setShowPhoneCapture(false); clearResume(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

const INK = COLOR.text;
const INK_MUTED = COLOR.textMuted;
const INK_FAINT = COLOR.textFaint;
const LINE = COLOR.glassBorder;
const styles = {
  page: { minHeight: '100vh', background: COLOR.bg, maxWidth: '430px', margin: '0 auto', color: INK, fontFamily: FONT, paddingBottom: '100px' },
  resumePage: { minHeight: '100vh', background: COLOR.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '18px', fontFamily: FONT, padding: '24px', textAlign: 'center' },
  resumeRing: { width: '44px', height: '44px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.12)', borderTopColor: COLOR.accent },
  resumeTitle: { fontSize: '20px', fontWeight: '700', color: INK, margin: 0 },
  resumeSub: { fontSize: '14px', color: INK_MUTED, margin: 0 },
  header: { display: 'flex', alignItems: 'center', padding: '52px 16px 16px', gap: '12px', position: 'sticky', top: 0, background: COLOR.bg, zIndex: 10 },
  backArrow: { background: COLOR.chipBg, border: `1px solid ${COLOR.glassBorder}`, borderRadius: '20px', padding: '7px 14px', color: COLOR.chipText, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  headerCenter: { flex: 1 },
  headerTitle: { fontSize: '22px', fontWeight: '700', color: INK, margin: '0 0 6px', letterSpacing: '-0.4px' },
  roomBadge: { display: 'inline-flex', alignItems: 'center', gap: '5px', background: COLOR.chipBg, border: `1px solid ${COLOR.chipBorder}`, borderRadius: '20px', padding: '4px 10px' },
  roomDot: { width: '5px', height: '5px', borderRadius: '50%', background: COLOR.accent, display: 'inline-block' },
  roomText: { fontSize: '11px', fontWeight: '600', color: COLOR.chipText },
  upsellBanner: { margin: '0 16px 12px', background: COLOR.glassBg, border: `1px solid ${LINE}`, borderRadius: '14px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
  upsellEmoji: { fontSize: '22px', flexShrink: 0 },
  upsellTitle: { fontSize: '13px', fontWeight: '700', color: INK, margin: '0 0 2px' },
  upsellSub: { fontSize: '11px', color: INK_MUTED, margin: 0 },
  upsellArrow: { fontSize: '20px', color: INK, fontWeight: '300' },
  categoryScroll: { overflowX: 'auto', position: 'sticky', top: '94px', background: COLOR.bg, zIndex: 9, borderBottom: `1px solid ${COLOR.hairline}` },
  categoryRow: { display: 'flex', padding: '0 16px', width: 'max-content', gap: '0' },
  categoryTab: { padding: '12px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', background: 'transparent', border: 'none', transition: 'all 0.2s ease' },
  grid: { padding: '16px' },
  gridInner: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  gridCard: { ...glass({ borderRadius: '16px' }), overflow: 'hidden', position: 'relative' },
  popularTag: { position: 'absolute', top: '8px', left: '8px', fontSize: '9px', fontWeight: '700', color: '#fff', background: COLOR.accent, border: 'none', borderRadius: '20px', padding: '3px 8px', zIndex: 1, letterSpacing: '0.3px' },
  imageBox: { width: '100%', aspectRatio: '1', background: 'linear-gradient(135deg, #1b1b20, #0d0d10)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: `1px solid ${COLOR.glassBorder}` },
  foodEmoji: { fontSize: '56px' },
  foodImg: { width: '100%', height: '100%', objectFit: 'cover' },
  cardInfo: { padding: '10px 10px 12px' },
  itemName: { fontSize: '13px', fontWeight: '700', color: INK, margin: '0 0 3px', lineHeight: 1.3 },
  itemDesc: { fontSize: '11px', color: INK_MUTED, margin: '0 0 10px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  itemPrice: { fontSize: '14px', fontWeight: '700', color: INK },
  qtyRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  qtyBtn: { width: '26px', height: '26px', borderRadius: '50%', background: COLOR.chipBg, border: `1px solid ${COLOR.glassBorder}`, color: INK, fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', lineHeight: 1 },
  qtyNum: { fontSize: '13px', fontWeight: '700', color: INK, minWidth: '14px', textAlign: 'center', display: 'inline-block' },
  addBtn: { width: '30px', height: '30px', borderRadius: '50%', background: COLOR.accent, border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', fontWeight: '700', lineHeight: 1 },
  cartBar: { position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', width: '390px', maxWidth: 'calc(100vw - 32px)', background: `linear-gradient(135deg, ${COLOR.accent} 0%, ${COLOR.accentDeep} 100%)`, borderRadius: '18px', padding: '15px 20px', display: 'flex', alignItems: 'center', cursor: 'pointer', boxShadow: `0 10px 30px ${COLOR.shadowDeep}, 0 0 40px rgba(232,0,29,0.25)`, zIndex: 100, gap: '12px' },
  cartCount: { width: '26px', height: '26px', borderRadius: '8px', background: '#fff', color: COLOR.accentDeep, fontSize: '13px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cartLabel: { fontSize: '15px', fontWeight: '700', color: '#fff', flex: 1 },
  cartTotal: { fontSize: '17px', fontWeight: '800', color: '#fff' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end' },
  modal: { ...glass({ borderRadius: '24px 24px 0 0' }), width: '100%', maxWidth: '430px', margin: '0 auto', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' },
  modalHandle: { width: '40px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', margin: '0 auto 20px' },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: INK, margin: '0 0 4px' },
  modalRoom: { fontSize: '13px', color: INK_MUTED, marginBottom: '20px' },
  modalScroll: { flex: 1, overflowY: 'auto', padding: '0 20px', WebkitOverflowScrolling: 'touch' },
  modalItems: { display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' },
  noteSection: { marginBottom: '20px' },
  noteLabel: { fontSize: '13px', fontWeight: '600', color: INK_MUTED, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  noteTextarea: { width: '100%', background: COLOR.chipBg, border: `1px solid ${COLOR.glassBorder}`, borderRadius: '12px', padding: '12px 14px', color: INK, fontSize: '14px', fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5 },
  modalFooter: { padding: '0 20px 36px', flexShrink: 0 },
  modalItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalItemLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  modalItemEmoji: { fontSize: '28px' },
  modalItemName: { fontSize: '14px', fontWeight: '600', color: INK, margin: '0 0 2px' },
  modalItemPrice: { fontSize: '12px', color: INK_MUTED, margin: 0 },
  modalDivider: { height: '1px', background: LINE, marginBottom: '16px' },
  modalTotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  modalTotalLabel: { fontSize: '14px', color: INK_MUTED },
  modalTotalAmount: { fontSize: '24px', fontWeight: '800', color: INK },
  checkoutBtn: { width: '100%', background: `linear-gradient(135deg, ${COLOR.accent} 0%, ${COLOR.accentDeep} 100%)`, color: '#fff', border: 'none', borderRadius: '14px', padding: '16px', fontSize: '16px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '10px' },
  continueBtn: { width: '100%', background: 'transparent', color: INK_MUTED, border: `1px solid ${COLOR.glassBorder}`, borderRadius: '14px', padding: '14px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  successPage: { minHeight: '100vh', background: COLOR.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center' },
  successIconWrap: { width: '80px', height: '80px', borderRadius: '24px', background: 'rgba(232,0,29,0.08)', border: '1px solid rgba(232,0,29,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' },
  successTitle: { fontSize: '30px', fontWeight: '700', color: INK, margin: '0 0 8px' },
  successSub: { fontSize: '14px', color: INK_MUTED, marginBottom: '28px' },
  successCard: { ...glass({ borderRadius: '20px', padding: '20px' }), width: '100%', maxWidth: '320px', marginBottom: '24px' },
  successRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' },
  successItemName: { fontSize: '14px', color: INK_MUTED },
  successItemPrice: { fontSize: '14px', color: INK_MUTED },
  successDivider: { height: '1px', background: LINE, margin: '8px 0' },
  successLabel: { fontSize: '10px', letterSpacing: '2px', color: INK_FAINT },
  successValue: { fontSize: '14px', fontWeight: '600', color: INK },
  backBtn: { background: COLOR.chipBg, border: `1px solid ${COLOR.glassBorder}`, borderRadius: '14px', padding: '14px 32px', color: INK, fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
};

export default GuestMenu;
